import { useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { FaWhatsapp } from 'react-icons/fa6';
import { LuBanknote, LuCalendarDays, LuClock, LuHash } from 'react-icons/lu';
import { Link, Navigate, useParams } from 'react-router';

import { useIdempotencyKey } from '@mds/shared/hooks/use-idempotency-key';
import { ApiError } from '@mds/shared/lib/api';
import { closedWeekdays, firstOpenDay } from '@mds/shared/lib/booking-slots';
import { clockRange, keyOf } from '@mds/shared/lib/dates';
import type {
  GuestBookingQuote,
  GuestBookingRequestReceipt,
  GuestSpaceDetail,
} from '@mds/shared/types/guest-booking';
import BookingCalendar, { type Selection } from '@mds/shared/ui/booking-calendar';
import { Alert } from '@mds/shared/ui/feedback/alert';
import { ChoiceList } from '@mds/shared/ui/form/choice-list';
import { Field } from '@mds/shared/ui/form/field';
import { Form, applyServerErrors, useZodForm } from '@mds/shared/ui/form/form';
import { FormSection } from '@mds/shared/ui/form/form-section';
import { PhoneInput } from '@mds/shared/ui/form/phone-input';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Input } from '@mds/shared/ui/shadcn/input';

import PolicyConsent from '@/components/form/policy-consent';
import PageHeader from '@/components/heading/page-header';
import OutcomePanel from '@/components/section/outcome-panel';
import { ErrorBlock, LoadingBlock } from '@/components/states';
import { BOOKING_POLICY, CONTACT } from '@/data';
import {
  PAYMENT_METHOD_LABELS,
  type DeskPaymentMethod,
} from '@/features/booking/desk-booking.schema';
import { roomBookingSchema, type RoomBooking } from '@/features/booking/room-booking.schema';
import { getQuote, requestBooking } from '@/features/spaces/spaces.api';
import { spaceQueries } from '@/features/spaces/spaces.queries';
import { formatLongDate, formatMoney } from '@/lib/format';
import NotFoundPage from './not-found';

const band = 'mx-auto max-w-page px-gutter pt-section';
/** The same capped column as the proposal form, centred with its header. */
const column = 'mx-auto box-content max-w-3xl px-gutter pt-section';

const PAYMENT_OPTIONS = Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({
  value,
  label,
}));

type SentRoomRequest = {
  receipt: GuestBookingRequestReceipt;
  values: RoomBooking;
  date: string;
  selection: Selection;
  whatsAppOpened: boolean;
};

/**
 * Booking a meeting room by the hour: the day and hours off the shared
 * month-and-slots calendar, then who is booking. "Book on WhatsApp" checks the
 * details, then hands the run to WhatsApp, already written, the same way the
 * desk booking does.
 */
export default function SpaceBookPage() {
  const { slug = '' } = useParams();
  const detail = useQuery(spaceQueries.detail(slug));

  if (detail.isError && detail.error instanceof ApiError && detail.error.status === 404) {
    return <NotFoundPage />;
  }
  if (detail.isPending || detail.isError) {
    return (
      <section className={band}>
        {detail.isPending ? (
          <LoadingBlock />
        ) : (
          <ErrorBlock
            title="We couldn't load this space"
            error={detail.error}
            onRetry={() => void detail.refetch()}
          />
        )}
      </section>
    );
  }
  const space = detail.data;
  // Any room can use direct requests; desks and proposal-mode rooms have their own flows.
  if (space.type === 'desk' || space.bookingMode !== 'direct_request') {
    return <Navigate to={`/spaces/${slug}`} replace />;
  }

  return (
    <section className={column}>
      <PageHeader
        back={{ label: space.name, to: `/spaces/${space.slug}` }}
        title={`Book ${space.name}`}
        description="Pick a day, then the hours you need. Click a later hour to extend your time."
      />
      <RoomBookingForm space={space} />
    </section>
  );
}

function RoomBookingForm({ space }: { space: GuestSpaceDetail }) {
  const closed = useMemo(() => closedWeekdays(space.operatingHours), [space.operatingHours]);
  const [date, setDate] = useState(() => firstOpenDay(keyOf(new Date()), closed));
  const [selection, setSelection] = useState<Selection | null>(null);
  const [sent, setSent] = useState<SentRoomRequest | null>(null);
  const [formError, setFormError] = useState<{ error: unknown; message?: string } | null>(null);
  const [newTotal, setNewTotal] = useState<number | null>(null);

  // Fresh for every day picked: an unpaid request holds nothing, so what was
  // free a minute ago may not be now. Switching days keeps the last board on
  // screen until the next one lands, so the calendar does not blink out.
  const board = useQuery({
    ...spaceQueries.availability(space.id, date),
    placeholderData: keepPreviousData,
  });

  // The same rule the calendar's own Continue uses: a time is picked, and it
  // is at least the room's minimum run.
  const minimum = space.minimumBookingMinutes;
  const canBook = selection !== null && (!minimum || selection.to - selection.from >= minimum);

  const form = useZodForm(roomBookingSchema, {
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      paymentMethod: '',
      policyAccepted: false,
    },
  });
  const policy = useController({ name: 'policyAccepted', control: form.control });
  const idempotency = useIdempotencyKey(`idem:room-booking-request:${space.id}`);

  function whatsAppUrl(
    values: RoomBooking,
    reference: string,
    bookedDate: string,
    slot: Selection,
  ) {
    const message = [
      "Hello MDS, I'd like to book a room.",
      '',
      `Reference: ${reference}`,
      `Room: ${space.name}`,
      `Date: ${formatLongDate(bookedDate)}`,
      `Time: ${clockRange(slot.from, slot.to)}`,
      '',
      `Name: ${values.fullName}`,
      `Email: ${values.email}`,
      `Phone: ${values.phone}`,
      ...(values.paymentMethod ? [`Payment: ${PAYMENT_METHOD_LABELS[values.paymentMethod]}`] : []),
    ].join('\n');
    return `${CONTACT.whatsappHref}?text=${encodeURIComponent(message)}`;
  }

  const bookingRequest = useMutation({
    mutationFn: async (values: RoomBooking) => {
      if (!selection || !canBook) throw new Error('Choose the hours you need.');
      const chosen = selection;
      const selectionBody = {
        resourceId: space.id,
        bookingDate: date,
        startMinute: chosen.from,
        endMinute: chosen.to,
      };
      const quote = await getQuote(selectionBody);
      const body = {
        ...selectionBody,
        guest: { name: values.fullName, email: values.email, phone: values.phone },
        paymentMethod: values.paymentMethod as DeskPaymentMethod,
        policyAccepted: true as const,
        expectedQuote: {
          rateCardVersionId: quote.price.rateCardVersionId,
          totalAmountMinor: quote.price.totalAmountMinor,
        },
      };
      const receipt = await requestBooking({ requestId: idempotency.keyFor(body), ...body });
      return { receipt, values, date, selection: chosen };
    },
    onMutate: () => {
      setFormError(null);
      setNewTotal(null);
    },
    onSuccess: (next) => {
      idempotency.reset();
      const tab = window.open(
        whatsAppUrl(next.values, next.receipt.reference, next.date, next.selection),
        '_blank',
      );
      if (tab) tab.opener = null;
      setSent({ ...next, whatsAppOpened: Boolean(tab) });
    },
    onError: (error) => {
      if (applyServerErrors(error, form.setError)) return;
      const code = error instanceof ApiError ? error.code : undefined;
      if (
        code === 'BOOKING_CONFLICT' ||
        code === 'BOOKING_OUTSIDE_OPERATING_HOURS' ||
        code === 'BOOKING_TIME_PAST'
      ) {
        setSelection(null);
        void board.refetch();
        setFormError({
          error,
          message:
            code === 'BOOKING_TIME_PAST'
              ? 'That time has passed. Pick a later one.'
              : 'Those hours are no longer available. Pick another time.',
        });
        return;
      }
      if (code === 'BOOKING_QUOTE_CHANGED') {
        const details = (error as ApiError).body?.details as
          { quote?: GuestBookingQuote } | undefined;
        if (details?.quote) {
          setNewTotal(details.quote.price.totalAmountMinor);
          return;
        }
      }
      if (code === 'BOOKING_REQUEST_ID_REUSED') {
        idempotency.reset();
        return setFormError({
          error,
          message: 'Your details changed since the last try. Press Book on WhatsApp again.',
        });
      }
      setFormError({ error });
    },
  });

  if (sent) {
    return (
      <OutcomePanel
        className="mt-block"
        tone={sent.whatsAppOpened ? 'success' : 'warning'}
        icon={sent.whatsAppOpened ? undefined : LuClock}
        title={sent.whatsAppOpened ? 'Booking request received' : 'One step left'}
        description={
          sent.whatsAppOpened
            ? 'Your request is saved. Complete the WhatsApp message so MDS can arrange payment and confirmation.'
            : 'Your request is saved. Open WhatsApp to send the prepared message to MDS.'
        }
        details={{
          title: space.name,
          items: [
            { icon: LuHash, marker: 'Reference', description: sent.receipt.reference },
            { icon: LuCalendarDays, marker: 'Date', description: formatLongDate(sent.date) },
            {
              icon: LuClock,
              marker: 'Time',
              description: clockRange(sent.selection.from, sent.selection.to),
            },
            {
              icon: LuBanknote,
              marker: 'Total',
              description: formatMoney(sent.receipt.price.totalAmountMinor),
            },
          ],
          note: 'Awaiting payment and confirmation. This request does not hold the room yet.',
        }}
        actions={
          <>
            <Button asChild size="lg">
              <a
                href={whatsAppUrl(sent.values, sent.receipt.reference, sent.date, sent.selection)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaWhatsapp aria-hidden="true" />
                Open WhatsApp
              </a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to={`/spaces/${space.slug}`}>Back to the space</Link>
            </Button>
          </>
        }
      />
    );
  }

  return (
    // When first, who last: the step that can fail (nothing free that day)
    // comes before anything is typed.
    <Form
      form={form}
      onSubmit={(values) => bookingRequest.mutate(values)}
      className="mt-block flex flex-col gap-card"
    >
      <FormSection title="Date and time" className="gap-4">
        {board.isPending ? (
          <LoadingBlock />
        ) : board.isError ? (
          <ErrorBlock
            title="We couldn't load the free hours"
            error={board.error}
            onRetry={() => void board.refetch()}
          />
        ) : (
          <BookingCalendar
            size="compact"
            disablePast
            closedWeekdays={closed}
            resource={{ name: space.name, minimumBookingMinutes: space.minimumBookingMinutes }}
            date={date}
            onDateChange={(next) => {
              setDate(next);
              // Hours are per day, so a run picked on another day means nothing here.
              setSelection(null);
              setFormError(null);
            }}
            slots={board.data.slots}
            openFromMinute={board.data.openFromMinute}
            openToMinute={board.data.openToMinute}
            selection={selection}
            onSelect={(next) => {
              setSelection(next);
              setFormError(null);
            }}
            showSummary
          />
        )}
      </FormSection>

      <FormSection title="Contact details" className="gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field name="fullName" label="Full name" required>
            <Input autoComplete="name" maxLength={160} />
          </Field>
          <Field name="email" label="Email" required>
            <Input type="email" autoComplete="email" />
          </Field>
          <Field name="phone" label="Phone / WhatsApp" required>
            <PhoneInput autoComplete="tel" />
          </Field>
          <Field name="paymentMethod" label="Payment method" required>
            <ChoiceList plain options={PAYMENT_OPTIONS} className="flex-row gap-x-4" />
          </Field>
        </div>

        <PolicyConsent
          policy={BOOKING_POLICY}
          checked={policy.field.value}
          onCheckedChange={policy.field.onChange}
          error={policy.fieldState.error?.message}
        />

        {newTotal !== null && (
          <Alert tone="warning" title="The price changed" onDismiss={() => setNewTotal(null)}>
            It is now {formatMoney(newTotal)}. Press Book on WhatsApp to send it at the new price.
          </Alert>
        )}

        {formError && (
          <ErrorBlock
            title="We couldn't send this booking request"
            error={formError.error}
            message={formError.message}
            onDismiss={() => setFormError(null)}
          />
        )}
      </FormSection>

      <div className="flex flex-wrap justify-center gap-3">
        <Button type="submit" disabled={!canBook || bookingRequest.isPending}>
          <FaWhatsapp aria-hidden="true" />
          {bookingRequest.isPending ? 'Sending request…' : 'Book on WhatsApp'}
        </Button>
        <Button asChild variant="outline">
          <Link to={`/spaces/${space.slug}`}>Back to the space</Link>
        </Button>
      </div>
    </Form>
  );
}
